import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'

// useConfirm — promise-returning confirm modal.
//
//   const confirm = useConfirm()
//   const ok = await confirm({
//     title: 'Delete event?',
//     body: 'This will remove all activities, components and notes.',
//     confirmLabel: 'Delete',
//     tone: 'destructive'
//   })
//   if (ok) doIt()
//
// Spec 17 §4 (CopyRosterDayModal), spec 21 (BulkDeleteLineItems), spec 22
// (Note delete) all consume this. The destructive tone swaps the confirm
// button to oxblood with a ⚠ icon — for any action whose effect can't be
// undone with a click.

export type ConfirmTone = 'default' | 'destructive'

export interface ConfirmOptions {
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
}

type Resolver = (ok: boolean) => void
interface State extends ConfirmOptions {
  resolver: Resolver
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State | null>(null)
  // Stable identity for the confirm function so consumers don't re-render
  // on every state change.
  const resolverRef = useRef<Resolver | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setState({ ...opts, resolver: resolve })
    })
  }, [])

  function settle(ok: boolean) {
    state?.resolver(ok)
    resolverRef.current = null
    setState(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmModal state={state} onSettle={settle} />}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm called outside <ConfirmProvider>')
  }
  return ctx
}

function ConfirmModal({
  state,
  onSettle
}: {
  state: State
  onSettle: (ok: boolean) => void
}) {
  const isDestructive = state.tone === 'destructive'
  const confirmClass = isDestructive
    ? 'px-4 py-2 rounded text-sm font-semibold bg-oxblood text-parchment hover:bg-oxblood/90'
    : 'px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90'

  return (
    <div
      onClick={() => onSettle(false)}
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-label={state.title}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md my-8 p-6"
      >
        <div className="flex items-center gap-2 mb-3">
          {isDestructive && (
            <span aria-hidden className="text-oxblood text-xl">
              ⚠
            </span>
          )}
          <h2 className="font-display text-xl">{state.title}</h2>
        </div>
        {state.body && (
          <div className="text-sm text-ink-soft mb-5">{state.body}</div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onSettle(false)}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            {state.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => onSettle(true)}
            className={confirmClass}
          >
            {state.confirmLabel ?? (isDestructive ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
